import { toProductPromptType } from '@/components/products/ProductsPromptModal/utils';
import { AppState } from '@/redux/helper';

import isPartnerCheckoutReturn from './core/arrival';
import resolveSpeedyFlow from './core/resolve';
import { setPendingAction } from './pendingAction';
import { setPendingResume, takePendingResume } from './pendingResume';
import speedyFlowSlice from './redux/slice';
import selectSpeedyFlowUser from './selectUser';
import { GateRequirement } from './types';

const { openSpeedyFlow: openSpeedyFlowAction } = speedyFlowSlice.actions;

/**
 * Fallback partner `utm_source` values, used only if remote config has not loaded.
 *
 * Partner slugs come from `partnerDomainsMap`, which already maps a partner's domain to its slug —
 * the same slug their links carry. Reading it there means adding a bundling partner is a config
 * change rather than a deploy. The fallback exists because an empty set would silently stop
 * recognising a partner checkout return, which is worse than being slightly out of date.
 */
const FALLBACK_PARTNER_SOURCES = ['torontozoo', 'tz'];

const partnerSourcesFrom = (state: AppState) => {
  const slugs = Object.values(state.remoteConfig?.partnerDomainsMap ?? {}).filter(Boolean);
  return new Set(slugs.length ? slugs : FALLBACK_PARTNER_SOURCES);
};

/**
 * Open the Speedy Flow for a gate.
 *
 * The trigger site says only what it *needs* — an account, a feature, access to a habitat,
 * optionally a named offer or an exact product — and this works out the rest: which link the
 * visitor arrived on, what they already hold, and therefore which flow (if any) runs. A gate the
 * visitor already satisfies and did not ask an offer for opens nothing, which is why call sites do
 * not have to check first. Returns whether a flow opened.
 *
 * `onSatisfied` is what the visitor was trying to do. It runs once, only if the flow ends with the
 * gate actually satisfied, and is deliberately a separate argument rather than a field on the gate:
 * the gate goes into redux, and a function there would break serializability.
 *
 * Takes state and dispatch rather than reading a store itself, so the handful of trigger sites that
 * are plain functions rather than components can use it too. Components should prefer the
 * `useOpenSpeedyFlow` hook.
 */
const openSpeedyFlow = (
  state: AppState,
  dispatch: (action: any) => void,
  gate: GateRequirement,
  onSatisfied?: () => void,
): boolean => {
  // Never restart a flow that is already running. Trigger sites re-fire as the user changes —
  // signing up mid-flow is exactly such a change — and re-opening would reset the machine,
  // throwing away a product the visitor had already chosen.
  if (state.modals.speedyFlow?.visible) {
    return false;
  }

  // A choice the visitor made before they were sent away to authenticate or pay, waiting for the
  // gate it belongs to. Only a gate that wants a purchase can carry it, and only if it does not
  // already name one of its own — an account gate has nowhere to put it, so it is left for the
  // gate that does.
  const resumed = gate.kind === 'account' || gate.product ? undefined : takePendingResume();
  const effectiveGate = resumed ? { ...gate, product: resumed } : gate;

  const { user } = state;
  const search = new URLSearchParams(window.location.search);
  // A gate that knows which offer it wants wins over the one on the URL.
  const ppt = effectiveGate.ppt ?? toProductPromptType(search.get('ppt') ?? undefined);
  // `ppto` rides beside `ppt` and is read the same way, from any page rather than only the ones
  // that happen to pass it explicitly — a campaign link is a URL, and which page it points at is
  // not the flow's business. Lowercased because the id is case-insensitive.
  const offerId = effectiveGate.offerId || search.get('ppto')?.toLowerCase() || undefined;

  // Attribution only — carried onto every event the flow fires, and never consulted by a decision.
  const utm = {
    source: search.get('utm_source') ?? undefined,
    medium: search.get('utm_medium') ?? undefined,
    campaign: search.get('utm_campaign') ?? undefined,
  };

  const partnerCheckoutReturn = isPartnerCheckoutReturn({
    utmSource: utm.source,
    utmCampaign: utm.campaign,
    partnerSources: partnerSourcesFrom(state),
  });

  const flowUser = selectSpeedyFlowUser(state);

  const resolution = resolveSpeedyFlow({
    gate: effectiveGate,
    isPartnerCheckoutReturn: partnerCheckoutReturn,
    ppt,
    user: flowUser,
  });
  if (resolution.flow === 'none') {
    // Nothing stands in their way, so there is nothing to resume — the caller does it themselves,
    // which is what the `false` return is for.
    //
    // The selection goes back where it came from first. A page may declare several gates, and this
    // one opened nothing: consuming it here would mean the gate that *does* want a purchase — very
    // possibly the next one to fire on the same page — resumes with nothing, and the returning
    // visitor is handed the product list they already worked through.
    if (resumed) {
      setPendingResume(resumed);
    }
    return false;
  }

  setPendingAction(onSatisfied);
  dispatch(
    openSpeedyFlowAction({
      resolution,
      gate: effectiveGate,
      utm,
      ppt,
      offerId,
      hasAccount: flowUser.isLoggedIn,
      hasPersona: !!flowUser.persona,
      email: user.email ?? undefined,
      productChoice: effectiveGate.product,
      openedAtPath: window.location.pathname,
      resumedChoice: !!resumed,
    }),
  );
  return true;
};

export default openSpeedyFlow;
